import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { useIsZigzag } from '@common/device-manager';
import { Typography } from '@common/styles';
import { isSupportedOpenExternalBrowser, openExternalBrowser } from '@common/utils';

import { BROWSER_HREF } from '../constants/link-url';

interface Props {
  show_policy_responsibility: boolean;
}

export const CatalogProductFooter = ({ show_policy_responsibility }: Props) => {
  const isZigzag = useIsZigzag();

  const handleEscrowLicenseClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isZigzag && isSupportedOpenExternalBrowser()) {
      e.preventDefault();
      openExternalBrowser(BROWSER_HREF.escrow_license);
    }
  };

  const handlePaymentGuaranteeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isZigzag && isSupportedOpenExternalBrowser()) {
      e.preventDefault();
      openExternalBrowser(BROWSER_HREF.payment_guarantee);
    }
  };

  return (
    <Container data-is-zigzag={isZigzag}>
      {show_policy_responsibility && (
        <Policy>
          <span className={Typography.CAPTION_11_REGULAR}>
            지그재그는 상품에 직접 관여하지 않으며 상품 주문, 배송 및<br />
            환불의 의무와 책임은 각 판매업체에 있습니다.
          </span>
        </Policy>
      )}
      <PolicyTitle>구매안전서비스</PolicyTitle>
      <LinkPolicy className={Typography.CAPTION_11_REGULAR}>
        <LinkPolicyText>
          당사는 고객님의 안전거래를 위해 관련 법률에 의거하여 나이스정보통신(주)의 에스크로 서비스를 적용하고 있습니다.
        </LinkPolicyText>
        <LinkPolicyLink
          className={Typography.CAPTION_12_SEMIBOLD}
          href={BROWSER_HREF.escrow_license}
          target='_blank'
          rel='noreferrer'
          onClick={handleEscrowLicenseClick}
        >
          서비스 가입사실 확인
        </LinkPolicyLink>
      </LinkPolicy>
      <LinkPolicy className={Typography.CAPTION_11_REGULAR}>
        <LinkPolicyText>
          당사는 고객님이 현금 결제한 금액에 대해 신한은행과 채무지급보증 계약을 체결하여 안전거래를 보장하고 있습니다.
        </LinkPolicyText>
        <LinkPolicyLink
          className={Typography.CAPTION_12_SEMIBOLD}
          href={BROWSER_HREF.payment_guarantee}
          target='_blank'
          rel='noreferrer'
          onClick={handlePaymentGuaranteeClick}
        >
          서비스 가입사실 확인
        </LinkPolicyLink>
      </LinkPolicy>
    </Container>
  );
};

const Container = styled.div`
  padding: 24px 16px 72px 16px;
  padding-bottom: calc(env(safe-area-inset-bottom) + 135px);
  padding-bottom: calc(constant(safe-area-inset-bottom) + 135px);
  display: flex;
  flex-direction: column;
  background-color: ${vars.color.backgroundOnElevated};

  &[data-is-zigzag='true'] {
    padding-bottom: calc(env(safe-area-inset-bottom) + 72px);
    padding-bottom: calc(constant(safe-area-inset-bottom) + 72px);
  }
`;

const Policy = styled.div`
  color: ${vars.color.textTertiary};
  margin-bottom: 16px;

  span {
    display: block;
    font-size: 11px;
    line-height: 140%;
  }
`;

const PolicyTitle = styled.p`
  padding: 0 0 8px 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${vars.color.gray550};
`;

const LinkPolicy = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const LinkPolicyText = styled.span`
  margin-right: 8px;
  color: ${vars.color.gray400};
`;

const LinkPolicyLink = styled.a`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-self: flex-start;
  padding: 5px 12px;
  border: 1px solid ${vars.color.borderSecondary};
  color: ${vars.color.textQuaternary};
  border-radius: 4px;
  flex-shrink: 0;

  &:link,
  &:active,
  &:visited {
    color: ${vars.color.textQuaternary};
  }
`;
